import { createContext, useContext, ReactNode } from 'react';
import { createMongoAbility, MongoAbility } from '@casl/ability';
import { createContextualCan } from '@casl/react';

// Match backend types
export type Subjects = 'Account' | 'Activity' | 'Report' | 'Settings' | 'User' | 'Organization' | 'all';
export type Actions = 'view' | 'create' | 'update' | 'delete' | 'export' | 'manage';
export type AppAbility = MongoAbility<[Actions, Subjects]>;

// Create ability context
const AbilityContext = createContext<AppAbility | undefined>(undefined);

// Create <Can> component for React
export const Can = createContextualCan(AbilityContext.Consumer as any);

interface AbilityProviderProps {
  children: ReactNode;
  ability: AppAbility;
}

/**
 * Provider for CASL abilities
 * Wraps app and provides ability instance to all components
 */
export function AbilityProvider({ children, ability }: AbilityProviderProps) {
  return (
    <AbilityContext.Provider value={ability}>
      {children}
    </AbilityContext.Provider>
  );
}

/**
 * Hook to access user abilities
 *
 * @example
 * const ability = useAbility();
 * if (ability.can('create', 'Account')) {
 *   // Show create button
 * }
 */
export function useAbility(): AppAbility {
  const ability = useContext(AbilityContext);
  if (!ability) {
    throw new Error('useAbility must be used within an AbilityProvider');
  }
  return ability;
}

/**
 * Build ability from rules (received from backend)
 */
export function buildAbility(rules: Array<{ action: string; subject: string; conditions?: Record<string, unknown> }>): AppAbility {
  return createMongoAbility<AppAbility>(rules as any);
}

/**
 * Create default ability (no permissions)
 */
export function createDefaultAbility(): AppAbility {
  return createMongoAbility<AppAbility>([]);
}
